/**
 * Global playback state. WatchPage publishes the currently-playing source
 * here; if the user navigates away from /watch the MiniPlayer reads it and
 * keeps the stream visible at bottom-right until X-ed or replaced.
 *
 * Holds:
 *   source       — what's currently playing (URL + metadata)
 *   position     — last reported video.currentTime, kept in a ref so it
 *                  doesn't trigger a re-render on every timeupdate tick
 *   seekTime     — for transmux seek-via-reload, the offset the stream
 *                  was last asked to start from. Persisting it here means
 *                  mini-player → watch page round-trips don't snap back
 *                  to 0:00.
 *
 * `setSource` resets seekTime + position automatically whenever the URL
 * base changes (i.e. the user picked different content). It treats the
 * `?seek=` / `&seek=` query string as cosmetic so the same content
 * re-published with a new seek doesn't trip the reset.
 */
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { chooseStreamUrl, type StreamMode, useStreamMode } from "../lib/streamMode";

export type SeekMode = "native" | "reload-transmux" | "reload-catchup";

export interface CatchupCtx {
  playlistId: number;
  streamId: number;
  origStartMs: number;
  durationMin: number;
}

export interface PlaybackSubtitle {
  url: string;
  lang: string;
  label: string;
  default?: boolean;
}

export interface PlaybackSource {
  url: string;
  altUrl?: string | null;
  engine?: "ts" | "hls" | "native";
  altEngine?: "ts" | "hls" | "native";
  poster?: string | null;
  title: string;
  isLive: boolean;
  forceDuration?: number | null;
  href: string;
  /** Stable identifier for the *content item* that doesn't change across seeks
   * or URL rewrites. e.g. "vod:1234" or "catchup:567". The context uses this to
   * decide whether a re-publish is "same content, new URL" (preserve seekTime
   * + position) or "different content" (reset everything). */
  contentKey: string;
  /** How seeking should behave for this content item.
   *  native        — set video.currentTime, byte-range to upstream
   *  reload-transmux — append `?seek=N` to baseProxyUrl, engine reloads
   *  reload-catchup  — refetch catchup-url with shifted start_ms, engine reloads */
  seekMode: SeekMode;
  /** Reload-transmux: the URL without the `?seek=N` query — mini-player rebuilds. */
  baseProxyUrl?: string;
  /** Reload-catchup: enough metadata to refetch a new timeshift URL. */
  catchupCtx?: CatchupCtx;
  /** Cast-targeted URL — for live channels, points at the cast HLS transcode
   * endpoint so the receiver gets H.264 + AAC regardless of upstream codec.
   * For VOD this is unset and we fall back to the regular cast logic. */
  castUrl?: string | null;
  /** Short-lived token appended to cast media URLs. The receiver fetches the
   *  stream itself and has none of our cookies, so without this it gets a 401
   *  and sits on its ambient screen having reported success. */
  castToken?: string | null;
  /** Subtitle tracks for the currently-playing content. The mini-player
   * passes these through so the picker stays populated after navigation. */
  subtitles?: PlaybackSubtitle[];
}

interface State {
  source: PlaybackSource | null;
  setSource: (s: PlaybackSource | null) => void;
  clear: () => void;
  position: number;
  reportPosition: (t: number) => void;
  seekTime: number;
  setSeekTime: (n: number) => void;
  /** True while a Chromecast session is active. Mini-player and PiP hide
   * themselves so the user only has one playback surface at a time. */
  casting: boolean;
  setCasting: (c: boolean) => void;
  /** Current preference for direct vs proxied URL selection. Read-only;
   * setters live in lib/streamMode (so a header chip / settings card can
   * mutate without going through context). */
  streamMode: StreamMode;
  /** Apply the URL-selection matrix to a (directUrl, proxyUrl) pair.
   * Returns the URL the player should actually load. Falls back to proxy
   * for HTTPS-page + HTTP-upstream even in "direct" mode (mixed content). */
  pickUrl: (input: { directUrl: string; proxyUrl: string | null }) => string;
}

const Ctx = createContext<State | null>(null);

export function PlaybackProvider({ children }: { children: ReactNode }) {
  const [source, setSourceState] = useState<PlaybackSource | null>(null);
  const [seekTime, setSeekTime] = useState(0);
  const [casting, setCasting] = useState(false);
  const positionRef = useRef(0);
  const [, force] = useState(0);
  const streamMode = useStreamMode();
  const pickUrl = useCallback(
    (urls: { directUrl: string; proxyUrl: string | null }) =>
      chooseStreamUrl(
        streamMode,
        urls,
        typeof window !== "undefined" && window.location.protocol === "https:",
      ),
    [streamMode],
  );

  const setSource = useCallback((s: PlaybackSource | null) => {
    setSourceState((prev) => {
      if (!s) {
        positionRef.current = 0;
        setSeekTime(0);
        return null;
      }
      const sameContent = !!prev && prev.contentKey === s.contentKey;
      if (!sameContent) {
        positionRef.current = 0;
        setSeekTime(0);
      }
      return s;
    });
  }, []);

  const clear = useCallback(() => {
    setSourceState(null);
    positionRef.current = 0;
    setSeekTime(0);
    force((n) => n + 1);
  }, []);

  const reportPosition = useCallback((t: number) => {
    positionRef.current = t;
  }, []);

  return (
    <Ctx.Provider
      value={{
        source,
        setSource,
        clear,
        get position() {
          return positionRef.current;
        },
        reportPosition,
        seekTime,
        setSeekTime,
        casting,
        setCasting,
        streamMode,
        pickUrl,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function usePlayback() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("usePlayback outside provider");
  return ctx;
}
